import { useState } from "react";
import api from "@/lib/api";
import { usePricingNudges, useInvalidate } from "@/lib/queries";
import { formatINR } from "@/lib/utils";
import { TrendUp, TrendDown, Sparkle, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Smart Pricing Alerts — proactive pricing nudges the host can apply in one tap.
 * Renders nothing when there are no open alerts.
 */
export default function PricingAlertsCard() {
  const { data: nudges = [] } = usePricingNudges();
  const invalidate = useInvalidate();
  const [busy, setBusy] = useState("");

  if (!nudges.length) return null;

  const act = async (id, kind) => {
    setBusy(id + kind);
    try {
      const { data } = await api.post(`/pricing/nudges/${id}/${kind}`);
      if (kind === "apply") {
        const n = data.dates || 0;
        const nights = `${n} night${n === 1 ? "" : "s"}`;
        toast.success(
          data.routed
            ? `Applied — sent to your channel manager (${nights})`
            : data.pushed
              ? `Applied ${formatINR(data.rate)} to ${nights} · pushed to OTAs`
              : `Applied ${formatINR(data.rate)} to ${nights}`
        );
      } else {
        toast.success("Alert dismissed");
      }
      invalidate.pricingNudges();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl border border-amber-200 dark:border-amber-800/50 bg-amber-50/60 dark:bg-amber-950/20 p-4" data-testid="pricing-alerts">
      <div className="flex items-center gap-2 mb-3">
        <Sparkle size={18} weight="fill" className="text-amber-500" />
        <h2 className="font-manrope text-sm font-bold text-slate-800 dark:text-white">Smart Pricing Alerts</h2>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-white">{nudges.length}</span>
      </div>
      <div className="space-y-2.5">
        {nudges.map((n) => {
          const up = n.direction === "up";
          const nights = n.dates?.length || 0;
          return (
            <div key={n.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 p-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${up ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"}`}>
                    {up ? <TrendUp size={10} weight="bold" /> : <TrendDown size={10} weight="bold" />}
                    {n.delta_pct > 0 ? `+${n.delta_pct}` : n.delta_pct}%
                  </span>
                  <span className="text-sm font-semibold text-slate-800 dark:text-white">{n.title}</span>
                  <span className="text-[11px] text-slate-400">{n.property_name}</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{n.message}</p>
                <p className="text-[11px] font-mono text-slate-600 dark:text-slate-300 mt-1">
                  {formatINR(n.current_rate)} →{" "}
                  <span className="font-bold text-amber-600 dark:text-amber-400">{formatINR(n.recommended_rate)}</span>
                  <span className="text-slate-400"> · {nights} night{nights === 1 ? "" : "s"}</span>
                  {n.dp_managed && <span className="text-slate-400"> · via channel manager</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => act(n.id, "apply")}
                  disabled={!!busy}
                  className="flex items-center gap-1 text-xs font-semibold bg-amber-400 hover:bg-amber-500 text-slate-900 rounded-lg px-3 py-1.5 transition disabled:opacity-50"
                >
                  <Check size={13} weight="bold" /> {busy === n.id + "apply" ? "Applying…" : "Apply"}
                </button>
                <button
                  onClick={() => act(n.id, "dismiss")}
                  disabled={!!busy}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 rounded-lg px-2 py-1.5 transition disabled:opacity-50"
                >
                  <X size={13} /> Dismiss
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
